<template>
  <div class="page_second" v-loading="loading">
    <div class="left">
      <div class="wallet-card-wrap">
        <div class="wallet-card" v-if="walletCards.length">
          <div class="wallet-card__head">
            <div class="wallet-card__badge">
              <span class="wallet-card__type">{{ currencyLabel }}</span>
              <span class="wallet-card__code">{{
                walletCards[0].currencyCode
              }}</span>
            </div>
            <button
              type="button"
              class="wallet-card__eye"
              :aria-label="show ? '隐藏余额' : '显示余额'"
              @click="showMoney(!show)"
            >
              <img :src="show ? openEyeIcon : closeEyeIcon" alt="" />
            </button>
          </div>
          <div class="wallet-card__body">
            <div class="wallet-card__label">可用余额</div>
            <div class="wallet-card__balance">
              {{ show ? formatAmount(walletCards[0].balance) : "******" }}
            </div>
          </div>
          <div class="wallet-card__foot">
            <span>账户尾号 {{ walletCards[0].tailNo }}</span>
            <span v-if="walletUpdatedAt">更新于 {{ walletUpdatedAt }}</span>
          </div>
        </div>
        <div class="wallet-card wallet-card--empty" v-else-if="!loading">
          <div class="wallet-card__label">可用余额</div>
          <div class="wallet-card__balance">—</div>
        </div>
      </div>
      <div class="money">
        <div class="title">游戏内货币余额</div>
        <div v-if="gameBalances.length === 0" class="game-empty">
          暂无分游戏流水记录
        </div>
        <div class="game-list" v-for="game in gameBalances" :key="game.gameId">
          <div class="game-item flex" @click="goGame(game.gameId)">
            <img class="img" :src="game.cover" alt="" />
            <div class="info">
              <div class="name-price flex flex-align-center">
                <span class="name">{{ game.name }}</span>
              </div>
              <div class="types flex">
                <div class="type-label">游戏金币</div>
              </div>
              <span class="go">GO</span>
            </div>
          </div>
          <div class="mark">
            <div class="text1">最近变动：{{ game.lastTime }}</div>
            <div class="text2">
              累计变动：{{ formatSignedAmount(game.totalChange) }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="right">
      <div class="search">
        <div class="paypall">
          <div class="dropdown">
            <span class="choose">
              {{ flowFilterLabel }}
              <img src="@/assets/images/svg/back.svg" alt="" class="img" />
            </span>
            <div class="dropdown-content">
              <a href="#" @click.prevent="setFlowFilter('all')">全部账单</a>
              <a href="#" @click.prevent="setFlowFilter('income')">收入</a>
              <a href="#" @click.prevent="setFlowFilter('expense')">支出</a>
            </div>
          </div>
        </div>
        <div class="time">
          <el-date-picker
            v-model="time"
            type="date"
            size="large"
            clearable
            placeholder="筛选日期"
            :teleported="false"
          />
        </div>
      </div>
      <div v-if="filteredFlows.length === 0" class="flows-empty">
        暂无账单记录
      </div>
      <div class="table_data" v-for="flow in filteredFlows" :key="flow.id">
        <div class="item" @click="openDetail(flow)">
          <div class="left">
            <div>{{ flowTitle(flow) }}</div>
            <div class="time">{{ formatFlowTime(flow.createdAt) }}</div>
          </div>
          <div
            class="right"
            :class="{
              'amount--plus': flow.changeAmount > 0,
              'amount--minus': flow.changeAmount < 0,
            }"
          >
            {{ formatSignedAmount(flow.changeAmount) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";
import { getWallet, getWalletFlows } from "@/api/gameCore";
import defaultGameCover from "@/assets/images/avatar.jpg";
import openEyeIcon from "@/assets/images/svg/open_eye.svg";
import closeEyeIcon from "@/assets/images/svg/close_eye.svg";

const FLOW_DETAIL_KEY = "wallet_flow_detail";

const show = ref(true);
const time = ref("");
const loading = ref(false);
const router = useRouter();
const $toast = useToast();

const CURRENCY_LABELS = {
  GOLD: "游戏金币",
};

const wallet = ref(null);
const flows = ref([]);
const flowFilter = ref("all");

const flowFilterLabel = computed(() => {
  const map = { all: "全部账单", income: "收入", expense: "支出" };
  return map[flowFilter.value] || "全部账单";
});

const currencyLabel = computed(() => {
  const code = wallet.value?.currencyCode || "GOLD";
  return CURRENCY_LABELS[code] || code;
});

const walletUpdatedAt = computed(() => {
  const raw = wallet.value?.updatedAt;
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const hour = String(d.getHours()).padStart(2, "0");
  const minute = String(d.getMinutes()).padStart(2, "0");
  return `${m}/${day} ${hour}:${minute}`;
});

const walletCards = computed(() => {
  if (!wallet.value) return [];
  const userId = wallet.value.userId || 0;
  const tail = String(userId).slice(-4).padStart(4, "0");
  return [
    {
      currencyCode: wallet.value.currencyCode || "GOLD",
      balance: wallet.value.balance ?? 0,
      tailNo: tail,
    },
  ];
});

const gameBalances = computed(() => {
  const map = new Map();
  flows.value.forEach((flow) => {
    const gameId = flow.gameId;
    if (!gameId) return;
    const prev = map.get(gameId) || {
      gameId,
      name: `游戏 #${gameId}`,
      cover: defaultGameCover,
      totalChange: 0,
      lastTime: "",
      lastAt: 0,
    };
    prev.totalChange += Number(flow.changeAmount) || 0;
    const ts = new Date(flow.createdAt).getTime();
    if (ts >= prev.lastAt) {
      prev.lastAt = ts;
      prev.lastTime = formatFlowTime(flow.createdAt);
    }
    map.set(gameId, prev);
  });
  return Array.from(map.values()).sort((a, b) => b.lastAt - a.lastAt);
});

const filteredFlows = computed(() => {
  return flows.value.filter((flow) => {
    if (flowFilter.value === "income" && flow.changeAmount <= 0) return false;
    if (flowFilter.value === "expense" && flow.changeAmount >= 0) return false;
    if (time.value) {
      const picked = new Date(time.value);
      const created = new Date(flow.createdAt);
      if (
        picked.getFullYear() !== created.getFullYear() ||
        picked.getMonth() !== created.getMonth() ||
        picked.getDate() !== created.getDate()
      ) {
        return false;
      }
    }
    return true;
  });
});

const formatAmount = (value) => {
  const num = Number(value) || 0;
  return num.toLocaleString("zh-CN");
};

const formatSignedAmount = (value) => {
  const num = Number(value) || 0;
  const prefix = num > 0 ? "+" : "";
  return `${prefix}${num.toLocaleString("zh-CN")}`;
};

const formatFlowTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const hour = String(d.getHours()).padStart(2, "0");
  const minute = String(d.getMinutes()).padStart(2, "0");
  return `${month}月${day}日 ${hour}:${minute}`;
};

const sceneLabelMap = {
  MATCH_REWARD: "对局奖励",
  MATCH_COST: "对局消耗",
  RECHARGE: "虚拟货币充值",
  CONSUME: "消费支出",
};

const flowTitle = (flow) => {
  if (flow.remark) return flow.remark;
  if (flow.scene && sceneLabelMap[flow.scene]) return sceneLabelMap[flow.scene];
  if (flow.scene) return flow.scene;
  return "账户变动";
};

const showMoney = (visible) => {
  show.value = visible;
};

const setFlowFilter = (type) => {
  flowFilter.value = type;
};

const openDetail = (flow) => {
  sessionStorage.setItem(FLOW_DETAIL_KEY, JSON.stringify(flow));
  router.push({ path: "/wallet/detail", query: { id: flow.id } });
};

const goGame = (gameId) => {
  if (gameId) router.push(`/_game/detail/${gameId}`);
};

const fetchWalletData = async () => {
  loading.value = true;
  try {
    const [walletRes, flowsRes] = await Promise.all([
      getWallet({ currencyCode: "GOLD" }),
      getWalletFlows(),
    ]);
    if (walletRes?.code === 200) {
      wallet.value = walletRes.data || null;
    }
    if (flowsRes?.code === 200) {
      flows.value = Array.isArray(flowsRes.data) ? flowsRes.data : [];
    }
  } catch (error) {
    console.error("获取钱包数据失败:", error);
    $toast.error("获取钱包数据失败", { position: "top" });
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchWalletData();
});
</script>
<style lang="scss" scoped>
.page_second {
  display: grid;
  grid-template-columns: minmax(320px, 633px) minmax(280px, 1fr);
  gap: 48px 80px;
  margin-top: 0;
  padding: 0 0 48px;
  align-items: start;

  .left {
    display: flex;
    flex-direction: column;
    min-width: 0;

    .wallet-card-wrap {
      width: 100%;
    }

    .wallet-card {
      width: 100%;
      min-height: 220px;
      padding: 28px 32px 24px;
      border-radius: var(--ios-radius-lg, 20px);
      border: 0.5px solid var(--ux-glass-border);
      background: linear-gradient(
        145deg,
        rgba(0, 212, 255, 0.14) 0%,
        var(--ux-glass-bg) 42%,
        rgba(167, 139, 250, 0.1) 100%
      );
      backdrop-filter: var(--ios-glass-blur);
      -webkit-backdrop-filter: var(--ios-glass-blur);
      box-shadow: var(--ux-glass-shadow);
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 28px;
    }

    .wallet-card__head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
    }

    .wallet-card__badge {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .wallet-card__type {
      font-size: 18px;
      font-weight: 600;
      letter-spacing: -0.02em;
      color: var(--ux-text-primary);
    }

    .wallet-card__code {
      font-size: 13px;
      font-weight: 500;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--ux-text-secondary, #9fb4d6);
    }

    .wallet-card__eye {
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      border: none;
      border-radius: var(--ios-radius-md, 14px);
      background: var(--ux-glass-bg-subtle);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.18s ease;

      img {
        width: 20px;
        height: 20px;
      }

      &:hover {
        background: var(--ios-fill-secondary);
      }
    }

    .wallet-card__body {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .wallet-card__label {
      font-size: 15px;
      font-weight: 500;
      color: var(--ux-text-secondary, #9fb4d6);
      letter-spacing: 0.02em;
    }

    .wallet-card__balance {
      font-size: clamp(36px, 4vw, 48px);
      font-weight: 700;
      line-height: 1.1;
      letter-spacing: -0.03em;
      color: var(--ux-text-primary);
      font-variant-numeric: tabular-nums;
    }

    .wallet-card__foot {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
      padding-top: 16px;
      border-top: 0.5px solid var(--ux-glass-border);
      font-size: 13px;
      color: var(--ux-text-muted, rgba(159, 180, 214, 0.65));
    }

    .wallet-card--empty .wallet-card__balance {
      font-size: 32px;
      opacity: 0.5;
    }
    .game-empty,
    .flows-empty {
      font-size: 15px;
      color: var(--ux-text-secondary, #9fb4d6);
      opacity: 0.85;
    }
    .money {
      margin-bottom: 42px;
      margin-top: 40px;
      .title {
        margin: 0 0 24px;
        font-family: Inter, Inter;
        font-weight: 400;
        font-size: 20px;
        color: var(--ux-text-primary);
        line-height: 23px;
        font-style: normal;
        text-transform: none;
      }
      .game-list {
        margin-top: 20px;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 14px;
        .mark {
          font-size: 16.002px;
          margin-left: 14px;
          color: var(--ux-text-secondary, #9fb4d6);
          .text1 {
            margin-bottom: 14px;
          }
        }

        .game-item {
          padding: 3px;
          width: 315px;
          height: 90.006px;
          background: var(--ux-card-bg);
          box-shadow: 0px 4.004px 23.996px 0px rgba(0, 0, 0, 0.12);
          border-radius: 23.996px 23.996px 23.996px 23.996px;
          box-sizing: border-box;
          cursor: pointer;
          position: relative;
          .go {
            position: absolute;
            top: 7px;
            right: 9.996px;
            width: 58.002px;
            height: 27.006px;
            line-height: 27.006px;
            border-radius: 34.006px 34.006px 34.006px 34.006px;
            border: 1px solid var(--ux-line);
            opacity: 0.5;
            text-align: center;
            font-family: Inter, Inter;
            font-weight: 400;
            font-size: 14px;
            color: var(--ux-text-primary);
            font-style: normal;
            text-transform: none;
          }

          .img {
            margin-right: 18.004px;
            width: 84.994px;
            height: 84.994px;
            border-radius: 23.996px;
            object-fit: cover;
          }

          .name-price {
            margin-top: 4.998px;
            font-size: 14px;
            line-height: 30.002px;
            margin-bottom: 4.998px;
            white-space: nowrap;

            .name {
              margin-right: 18.004px;
              font-size: 16.002px;
              font-family: Inter-Regular, Inter;
              color: var(--ux-text-primary);
            }
          }

          .type-label {
            min-width: 70px;
            height: 29px;
            line-height: 29px;
            background: var(--ux-surface-muted);
            border-radius: 6px;
            font-size: 14px;
            font-family: Inter-Regular, Inter;
            color: var(--ux-text-secondary, #9fb4d6);
            text-align: center;

            &:not(:first-child) {
              margin-left: 11px;
            }
          }
        }
      }
    }
  }

  .right {
    min-width: 0;
    .flows-empty {
      margin-top: 24px;
      text-align: center;
      padding: 48px 0;
    }
    .search {
      display: flex;
      .dropdown {
        position: relative;
        display: inline-block;
        cursor: pointer;
        width: 125.006px;
        height: 41.006px;
        font-size: 20px;
        background: var(--ux-glass-bg);
        border-radius: 15px 15px 15px 15px;
        text-align: center;
        line-height: 41.006px;

        &:hover .img {
          transform: rotate(-90deg);
        }
        .choose {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: 16px;
          color: var(--ux-text-primary);
          .img {
            width: 15px;
            height: 15px;
            transition: transform 0.3s ease;
          }
        }
        .dropdown-content {
          display: none;
          position: absolute;
          background-color: var(--ux-glass-bg-elevated);
          min-width: 100px;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          z-index: 1;
          left: 50%;
          top: 41.006px;
          transform: translateX(-50%);
          border-radius: 10px;
          overflow: hidden;
          a {
            display: block;
            width: 105.994px;
            height: 39.004px;
            text-decoration: none;
            color: var(--ux-text-primary);
            text-align: center;
            line-height: 39.004px;
            font-size: 14px;
            &:hover {
              background: var(--ios-fill-secondary);
            }
          }
        }
      }

      .dropdown:hover .dropdown-content {
        display: block;
      }
      .time {
        margin-left: 56px;
      }
    }
    .table_data {
      margin-top: 10px;
      width: 100%;
      max-width: 420px;
      min-height: 68px;
      background: var(--ux-glass-bg);
      border: 0.5px solid var(--ux-glass-border);
      border-radius: 12px;
      .item {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        min-height: 68px;
        padding: 10px 0;
        cursor: pointer;
        .left {
          padding: 0 0 0 14px;
          font-weight: 400;
          font-size: 16px;
          color: var(--ux-text-primary);
          line-height: 19px;
          letter-spacing: 0.5px;
          text-align: left;
          font-style: normal;
          text-transform: none;
          .time {
            font-size: 14px;
            margin-top: 8.4px;
            color: var(--ux-text-secondary, #9fb4d6);
          }
        }
        .right {
          padding-right: 14px;
          font-weight: 600;
          font-size: 16px;
          color: var(--ux-text-primary);
          line-height: 19px;
          letter-spacing: 0.5px;
          text-align: right;
          font-style: normal;
          text-transform: none;
        }
        .amount--plus {
          color: #4ade80;
        }
        .amount--minus {
          color: #f87171;
        }
      }
    }
  }
}

@media (max-width: 1200px) {
  .page_second {
    grid-template-columns: 1fr;
    gap: 32px;
  }
}

@media (max-width: 768px) {
  .page_second {
    padding-bottom: 40px;
  }
}
</style>
